import env from '@/env';
import isEmpty from 'lodash/isEmpty';
import createAuthorizedApiClient, { AuthTokenType } from '../authorized-api-client';

export interface EntityForm {
  organisationId: string;
  entityType?: string;
  name: string;
  logo?: File;
  primaryColor: string;
  accentColor: string;
  description?: string;
  landingPageTitleLine1?: string;
  landingPageTitleLine2?: string;
  landingPageTitleLine3?: string;
  landingPageWelcomeHeaderText?: string;
  landingPageWelcomeBodyText?: string;
  landingPageAppIntroHeaderText?: string;
  landingPageAppIntroBodyText?: string;
  landingPageApiIntroHeaderText?: string;
  landingPageApiIntroBodyText?: string;
  landingPageApiIntroPendingText?: string;
  landingPageNewsIntroText?: string;
  showApiOverview?: boolean;
}

export interface EntityApiOverviewForm {
  showApiOverview: boolean;
}

export interface AppFeatures {
  featureId: string;
  description: string;
}
export interface AppInfoForm {
  name: string;
  description: string;
  appFeatures: string;
  published: boolean;
  sequence: number;
  appImageList: File[];
  imageUrl: string[];
  appStoreLink: string;
  playStoreLink: string;
  iOSBetaLink: string;
  AndroidBetaLink: string;
  demoVideo: string;
  appStoreRating: number;
  playStoreRating: number;
}

export interface ArticleForm {
  id: string;
  title: string;
  sourceUrl: string;
  content: string;
  imageUrl: string;
  author: string;
  summary: string;
  publishedAt: string;
  tags: string[];
  isFeatured: boolean;
  isActive: boolean;
  isPublic: boolean;
  isGlobal: boolean;
}

export interface RateAssetForm {
  rateType: string;
  ratedAsset: string;
  ratedId: string;
  ratedScore: number;
  comments?: string;
}

export interface CategoryForm {
  entryType: string;
  name: string;
  industry: string;
  description: string;
  icon: string;
  order: number;
}

export const serviceDirectoryApisClient = createAuthorizedApiClient(
  env.api.baseUrl.apiPortal,
  AuthTokenType.AppOrUserAccessToken,
  true,
);

class ServiceDirectoryService {
  fetchEntity = async (subdomain: string) => {
    const response = await serviceDirectoryApisClient.get(`entities/subdomain/${subdomain}`);
    return response.data;
  };

  fetchListEntity = async (pageNumber: number, pageSize: number, searchEntity: string) => {
    const response = await serviceDirectoryApisClient.get(`entities`, {
      params: {
        pageNumber,
        pageSize,
        listOrders: 'updatedAt-desc',
        searchText: searchEntity,
      },
    });
    return response.data;
  };

  fetchEntityById = async (entityId: string) => {
    const response = await serviceDirectoryApisClient.get(`entities/${entityId}`);
    return response.data;
  };

  deleteEntity = async (entityId: string) => {
    const response = await serviceDirectoryApisClient.delete(`entities/${entityId}`);
    return response.data;
  };

  createOrUpdateEntity = async (entity: EntityForm) => {
    const formData = this.formDataFromEntity(entity, env.siteId);
    const response = await serviceDirectoryApisClient.put('entities', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        organisationId: entity.organisationId,
      },
    });

    return response.data;
  };

  fetchAppsInfo = async (organisationId: string) => {
    const response = await serviceDirectoryApisClient.get('apps', {
      headers: {
        'Content-Type': 'multipart/form-data',
        organisationId,
      },
    });

    return response.data;
  };

  fetchArticles = async (
    organisationId: string | undefined,
    isGlobal: boolean,
    pageNumber: number,
    pageSize: number,
    searchText?: string,
  ) => {
    const response = await serviceDirectoryApisClient.get('articles', {
      params: { pageNumber, pageSize, isGlobal, searchText },
      headers: {
        organisationId,
      },
    });

    return response.data;
  };

  fetchArticlesBySlug = async (slug: string) => {
    const response = await serviceDirectoryApisClient.get(`articles/${slug}`);
    return response.data;
  };

  createArticle = async (article: ArticleForm, organisationId: string) => {
    const formData = this.formDataArticle(article);
    const response = await serviceDirectoryApisClient.post('articles', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        organisationId,
      },
    });

    return response.data;
  };

  updateArticle = async (id: string, article: ArticleForm, organisationId: string) => {
    const formData = this.formDataArticle(article);
    const response = await serviceDirectoryApisClient.put(`articles/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        organisationId,
      },
    });
    return response.data;
  };

  deleteArticle = async (id: string, organisationId: string) => {
    const response = await serviceDirectoryApisClient.delete(`articles/${id}`, {
      headers: { organisationId },
    });
    return response.data;
  };

  fetchCategories = async (entryType?: string, industry?: string) => {
    const response = await serviceDirectoryApisClient.get(
      `categories?entryType=${entryType || ''}&industry=${industry || ''}`,
    );
    return response.data;
  };

  fetchCategory = async (id: string) => {
    const response = await serviceDirectoryApisClient.get(`categories/${id}`);
    return response.data;
  };

  createCategory = async (category: CategoryForm) => {
    const formData = this.formDataCategory(category);
    const response = await serviceDirectoryApisClient.post('/categories', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  };

  updateCategory = async (id: string, category: CategoryForm) => {
    const formData = this.formDataCategory(category);
    const response = await serviceDirectoryApisClient.put(`/categories/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  };

  deleteCategory = async (id: string) => {
    const response = await serviceDirectoryApisClient.delete(`/categories/${id}`);
    return response.data;
  };

  createAppInfo = async (appInfo: AppInfoForm, organisationId: string) => {
    const formData = this.formDataAppInfo(appInfo);

    const response = await serviceDirectoryApisClient.post('apps', formData, {
      headers: { 'Content-Type': 'multipart/form-data', organisationId },
    });

    return response.data;
  };

  updateAppInfo = async (id: string, appInfo: AppInfoForm, organisationId: string) => {
    const formData = this.formDataAppInfo(appInfo);
    const response = await serviceDirectoryApisClient.put(`apps/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data', organisationId },
    });
    return response.data;
  };

  deleteAppInfo = async (id: string, organisationId: string) => {
    const response = await serviceDirectoryApisClient.delete(`apps/${id}`, {
      headers: { organisationId },
    });
    return response.data;
  };

  scrapeAppInfo = async (id: string, appStoreLink?: string, playStoreLink?: string) => {
    const response = await serviceDirectoryApisClient.post(`apps/${id}/scrape`, {
      appStoreLink,
      playStoreLink,
    });
    return response.data;
  };

  fetchListUserSocial = async (
    pageNumber: number,
    pageSize: number,
    sort?: string,
    searchText?: string,
  ) => {
    const response = await serviceDirectoryApisClient.get(`visitors`, {
      params: {
        pageNumber,
        pageSize,
        searchText,
        sort,
      },
    });
    return response.data;
  };

  rateEntity = async (entityId: string, rating: number) => {
    const response = await serviceDirectoryApisClient.post(`entities/${entityId}/rate`, { rating });
    return response.data;
  };

  rateAsset = async (formRateAsset: RateAssetForm, socialUserToken: string) => {
    const response = await serviceDirectoryApisClient.post(
      `visitors/ratings`,
      { ...formRateAsset },
      {
        headers: {
          socialAuthorization: socialUserToken,
        },
      },
    );
    return response.data;
  };

  fetchAssetSocial = async (socialUserToken: string) => {
    const response = await serviceDirectoryApisClient.get(`visitors/ratings`, {
      headers: {
        socialAuthorization: socialUserToken,
      },
    });
    return response.data;
  };

  exchangeSocialToken = async (socialToken: string, socialNetwork: string) => {
    const response = await serviceDirectoryApisClient.post('visitors', {
      accessToken: socialToken,
      socialNetwork,
    });
    return response.data;
  };

  exchangeSocialTokenLinkedin = async (code: string) => {
    const response = await serviceDirectoryApisClient.get(
      `visitors/exchange-token-linkedin?code=${code}`,
    );
    return response.data;
  };

  generateIndexes = async () => {
    const response = await serviceDirectoryApisClient.post('indexes');
    return response.data;
  };

  fetchComplianceLevels = async () => {
    const response = await serviceDirectoryApisClient.get(`compliance-levels`);
    return response.data;
  };

  fetchDocsOverview = async (organisationId: string) => {
    const response = await serviceDirectoryApisClient.get(`docs`, {
      headers: {
        organisationId,
      },
    });
    return response.data;
  };

  updateDocOverview = async (organisationId: string, tags: string, docString: string) => {
    const body = { tags, docString };
    const response = await serviceDirectoryApisClient.post(`docs`, body, {
      headers: {
        organisationId,
      },
    });
    return response.data;
  };

  private formDataAppInfo = (appInfo: AppInfoForm) => {
    const formData = new FormData();
    formData.append('name', appInfo.name);
    if (!isEmpty(appInfo.appImageList)) {
      appInfo.appImageList.forEach((image) => {
        image && formData.append('appImageList', image);
      });
    }
    formData.append('appFeatures', appInfo.appFeatures ?? '');
    formData.append('description', appInfo.description ?? '');
    formData.append('sequence', appInfo.sequence.toString());
    formData.append('published', appInfo.published.toString());
    formData.append('appStoreLink', appInfo.appStoreLink ?? '');
    formData.append('playStoreLink', appInfo.playStoreLink ?? '');
    formData.append('iOSBetaLink', appInfo.iOSBetaLink ?? '');
    formData.append('AndroidBetaLink', appInfo.AndroidBetaLink ?? '');
    formData.append('demoVideo', appInfo.demoVideo ?? '');
    appInfo.appStoreRating && formData.append('appStoreRating', appInfo.appStoreRating.toString());
    appInfo.playStoreRating &&
      formData.append('playStoreRating', appInfo.playStoreRating.toString());
    appInfo.imageUrl && formData.append('imageUrl', JSON.stringify(appInfo.imageUrl));
    return formData;
  };

  private formDataArticle = (article: ArticleForm) => {
    const formData = new FormData();
    formData.append('title', article.title);
    if (typeof article.imageUrl === 'string') {
      formData.append('imageUrl', article.imageUrl);
    } else {
      formData.append('imageFile', article.imageUrl);
    }
    formData.append('content', article.content ?? '');
    formData.append('sourceUrl', article.sourceUrl ?? '');
    formData.append('summary', article.summary ?? '');
    formData.append('author', article.author ?? '');
    formData.append('publishedAt', article.publishedAt ?? '');
    formData.append('tags', article.tags.join(','));
    formData.append('isFeatured', article.isFeatured.toString());
    formData.append('isActive', article.isActive.toString());
    formData.append('isPublic', article.isPublic.toString());
    formData.append('isGlobal', article.isGlobal.toString());
    return formData;
  };

  private formDataCategory = (category: CategoryForm) => {
    const formData = new FormData();
    formData.append('name', category.name);
    if (typeof category.icon === 'string') {
      formData.append('iconUrl', category.icon);
    } else {
      formData.append('icon', category.icon);
    }
    formData.append('industry', category.industry ?? '');
    formData.append('entryType', category.entryType ?? '');
    formData.append('order', category.order.toString() ?? '');
    formData.append('description', category.description ?? '');
    return formData;
  };

  private formDataFromEntity = (entity: EntityForm, appId: string) => {
    const formData = new FormData();
    formData.append('appId', appId);
    formData.append('name', entity.name);
    formData.append('entityType', entity.entityType || 'Bank');
    entity.logo && formData.append('logo', entity.logo);
    formData.append('primaryColor', entity.primaryColor ?? '');
    formData.append('accentColor', entity.accentColor ?? '');
    formData.append('description', entity.description ?? '');
    formData.append('landingPageTitleLine1', entity?.landingPageTitleLine1 ?? '');
    formData.append('landingPageTitleLine2', entity?.landingPageTitleLine2 ?? '');
    formData.append('landingPageTitleLine3', entity?.landingPageTitleLine3 ?? '');
    formData.append('landingPageWelcomeHeaderText', entity?.landingPageWelcomeHeaderText ?? '');
    formData.append('landingPageWelcomeBodyText', entity?.landingPageWelcomeBodyText ?? '');
    formData.append('landingPageAppIntroHeaderText', entity?.landingPageAppIntroHeaderText ?? '');
    formData.append('landingPageAppIntroBodyText', entity?.landingPageAppIntroBodyText ?? '');
    formData.append('landingPageApiIntroHeaderText', entity?.landingPageApiIntroHeaderText ?? '');
    formData.append('landingPageApiIntroBodyText', entity?.landingPageApiIntroBodyText ?? '');
    formData.append('landingPageApiIntroPendingText', entity?.landingPageApiIntroPendingText ?? '');
    formData.append('landingPageNewsIntroText', entity?.landingPageNewsIntroText ?? '');
    return formData;
  };

  createAndScrapeAppInfo = async (organisationId: string, urlScrape: string) => {
    const response = await serviceDirectoryApisClient.post(
      `apps/scrape`,
      {
        urlScrape,
      },
      {
        headers: {
          organisationId,
        },
      },
    );
    return response.data;
  };

  fetchFeatureItem = async () => {
    const response = await serviceDirectoryApisClient.get('appFeatures');
    return response.data;
  };
}

const instance = new ServiceDirectoryService();
export default instance;
